import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown, Check } from "lucide-react";
import { GEOGRAPHIES } from "@/i18n";

// Maps locale code → human label for the trigger badge.
const LOCALE_BADGE = {
  en: "EN", hi: "हि", mr: "म", bn: "বা", id: "ID", sw: "SW",
};

// Find which geography contains the given locale (used to restore state on reload).
function findGeoForLocale(locale) {
  for (const g of GEOGRAPHIES) {
    if (g.languages.some((l) => l.code === locale)) return g;
  }
  return GEOGRAPHIES[0];
}

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || "en").slice(0, 2);
  const [open, setOpen] = useState(false);
  const [pickedGeo, setPickedGeo] = useState(null);
  // Derived: explicit user pick wins, otherwise infer from current locale
  const geo = pickedGeo || findGeoForLocale(current);
  const ref = useRef(null);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pickLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("cg.lang", code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="lang-switcher-trigger"
        aria-label="Language and region"
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/60 px-3 py-2 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors"
      >
        <Globe size={13} className="text-signal-lime" />
        <span aria-hidden className="text-base leading-none">{geo.flag}</span>
        <span className="hidden sm:inline">{LOCALE_BADGE[current] || current.toUpperCase()}</span>
        <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[280px] z-50 rounded-2xl border border-border bg-popover/95 backdrop-blur-xl shadow-2xl"
          data-testid="lang-switcher-panel"
        >
          <div className="px-4 pt-4 pb-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Region</p>
          </div>
          <div className="px-2">
            {GEOGRAPHIES.map((g) => (
              <button
                key={g.code}
                type="button"
                onClick={() => setPickedGeo(g)}
                data-testid={`lang-geo-${g.code}`}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${
                  geo.code === g.code ? "bg-secondary/70 text-foreground" : "text-muted-foreground hover:bg-secondary/40 hover:text-foreground"
                }`}
              >
                <span aria-hidden className="text-lg leading-none">{g.flag}</span>
                <span className="flex-1 text-left">{g.label}</span>
                {geo.code === g.code && <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-signal-lime">selected</span>}
              </button>
            ))}
          </div>

          <div className="mt-2 border-t border-border/60 px-4 pt-3 pb-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Language</p>
          </div>
          <div className="px-2 pb-3">
            {geo.languages.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => pickLanguage(l.code)}
                data-testid={`lang-pick-${l.code}`}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors ${
                  current === l.code ? "bg-signal-lime/10 text-signal-lime" : "text-muted-foreground hover:bg-secondary/40 hover:text-foreground"
                }`}
              >
                <span>{l.label}</span>
                {current === l.code && <Check size={14} className="text-signal-lime" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
